import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — legaldecoder" },
      {
        name: "description",
        content:
          "Terms governing use of legaldecoder. Not legal advice — for informational purposes only.",
      },
      { property: "og:title", content: "Terms of Service — legaldecoder" },
      {
        property: "og:description",
        content: "Terms of service for using legaldecoder. Informational purposes only.",
      },
      { property: "og:url", content: "https://legaldecoder.io/terms" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/terms" }],
  }),
  component: Terms,
});

function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold tracking-tight">Terms of Service</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: April 2026</p>

        <div className="mt-10 rounded-2xl border-2 border-warning/30 bg-warning/5 p-6">
          <h2 className="text-lg font-bold">⚠️ Not Legal Advice</h2>
          <p className="mt-2 text-foreground">
            legaldecoder provides AI-powered contract analysis for{" "}
            <strong>informational purposes only</strong>. We are not a law firm and do not provide
            legal advice. Always consult a licensed attorney for legal decisions.
          </p>
        </div>

        <div className="prose prose-slate mt-8 max-w-none space-y-8 text-foreground">
          <section>
            <h2 className="text-xl font-semibold">1. Who you're contracting with</h2>
            <p className="mt-2 text-muted-foreground">
              These Terms of Service ("Terms") are a legal agreement between you and{" "}
              <strong>legaldecoder</strong> ("legaldecoder", "we", "us", or "our"), operating the
              website at legaldecoder.io and the related legaldecoder service (the "Service"). By
              creating an account, accessing, or using the Service, you agree to these Terms. If you
              do not agree, do not use the Service.
            </p>
            <p className="mt-2 text-muted-foreground">
              If you are using the Service on behalf of an organisation, you represent that you have
              authority to bind that organisation. If you are using the Service as an individual,
              you confirm that you are of legal age in your jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">2. The Service</h2>
            <p className="mt-2 text-muted-foreground">
              legaldecoder is an AI-powered tool that analyses contracts and other legal documents
              that you submit, summarises them in plain English, and highlights potentially risky
              clauses. Outputs are generated by AI models and may be inaccurate, incomplete, or out
              of date. The Service is not a substitute for advice from a qualified attorney.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">
              3. Payments, subscriptions and Merchant of Record
            </h2>
            <p className="mt-2 text-muted-foreground">
              Our order process is conducted by our online reseller <strong>Paddle.com</strong>.
              Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer
              service inquiries and handles returns. By purchasing a subscription, you also agree to
              Paddle's Buyer Terms, available at{" "}
              <a
                className="underline"
                href="https://www.paddle.com/legal/checkout-buyer-terms"
                target="_blank"
                rel="noreferrer"
              >
                paddle.com/legal/checkout-buyer-terms
              </a>
              .
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6 text-muted-foreground">
              <li>
                <strong>Plans &amp; billing.</strong> Free accounts include a limited number of
                analyses per month. Paid subscriptions ("Pro") are billed monthly or annually in
                advance via Paddle, in the currency shown at checkout.
              </li>
              <li>
                <strong>Renewals.</strong> Subscriptions renew automatically at the end of each
                billing period at the then-current price unless cancelled before the renewal date.
              </li>
              <li>
                <strong>Cancellation.</strong> You may cancel your subscription at any time from
                your account page or via the Paddle customer portal at{" "}
                <a className="underline" href="https://paddle.net" target="_blank" rel="noreferrer">
                  paddle.net
                </a>
                . Cancellation takes effect at the end of the current paid period; you retain Pro
                access until then.
              </li>
              <li>
                <strong>Taxes.</strong> Prices may exclude applicable VAT, GST or sales tax, which
                Paddle collects and remits where required.
              </li>
              <li>
                <strong>Refunds.</strong> Refunds are governed by our{" "}
                <a className="underline" href="/refund">
                  Refund Policy
                </a>{" "}
                and processed by Paddle.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">4. Your account</h2>
            <p className="mt-2 text-muted-foreground">
              You must provide accurate information when registering and keep it up to date. You are
              responsible for keeping your account credentials confidential and for all activity
              under your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">5. Acceptable use</h2>
            <p className="mt-2 text-muted-foreground">You agree not to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-muted-foreground">
              <li>use the Service for unlawful, fraudulent, or abusive purposes;</li>
              <li>
                upload content you do not have the right to share, or that infringes third-party
                intellectual property, privacy, or confidentiality;
              </li>
              <li>
                attempt to interfere with the security or integrity of the Service (including
                malware, probing, scraping, or circumventing rate limits);
              </li>
              <li>
                resell, sublicense, or redistribute the Service or its outputs as a competing
                product;
              </li>
              <li>
                reverse engineer, decompile, or attempt to extract the underlying models or source
                code;
              </li>
              <li>
                use the Service to generate illegal content, hate speech, harassment, or material
                that violates the rights of others.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">6. AI-generated outputs</h2>
            <p className="mt-2 text-muted-foreground">
              You are responsible for the documents you submit and for how you use the Service's
              outputs. Outputs are generated by third-party AI models and may contain errors. You
              must independently verify any output before relying on it, and you should not use the
              Service as a substitute for advice from a qualified legal professional in regulated
              matters. We may filter, restrict, or refuse outputs that violate our acceptable use
              policy. If you believe an output infringes your rights, contact us at{" "}
              <a className="underline" href="mailto:legal@legaldecoder.io">
                legal@legaldecoder.io
              </a>{" "}
              and we will review takedown requests in good faith.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">7. Intellectual property</h2>
            <p className="mt-2 text-muted-foreground">
              The Service, including its software, design, branding, and documentation, is owned by
              legaldecoder and its licensors and is protected by intellectual property laws. We
              grant you a limited, non-exclusive, non-transferable, revocable right to use the
              Service in accordance with your plan and these Terms. You retain ownership of the
              documents you upload; you grant us a limited licence to host and process them solely
              to provide the Service to you.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">
              8. Service availability and warranty disclaimer
            </h2>
            <p className="mt-2 text-muted-foreground">
              The Service is provided on an "as is" and "as available" basis. We do not guarantee
              that the Service will be uninterrupted, error-free, or free from security
              vulnerabilities. To the fullest extent permitted by law, we disclaim all implied
              warranties, including merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">9. Limitation of liability</h2>
            <p className="mt-2 text-muted-foreground">
              To the fullest extent permitted by law, our aggregate liability arising out of or in
              connection with these Terms or the Service is limited to the amounts paid by you to us
              (via Paddle) in the twelve (12) months preceding the event giving rise to the claim.
              We will not be liable for indirect, incidental, special, consequential, or punitive
              damages, including loss of profits, revenue, data, or goodwill. Nothing in these Terms
              excludes liability for fraud, death, personal injury, or any liability that cannot be
              excluded by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">10. Indemnity</h2>
            <p className="mt-2 text-muted-foreground">
              You agree to indemnify and hold legaldecoder harmless from claims, damages, and
              expenses arising from content you submit, your unlawful use of the Service, or your
              breach of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">11. Suspension and termination</h2>
            <p className="mt-2 text-muted-foreground">
              We may suspend or terminate your access to the Service if you materially breach these
              Terms, fail to pay amounts due, create security or fraud risk, or repeatedly violate
              our acceptable use policy. You may stop using the Service at any time. On termination,
              your right to use the Service ends; we may delete account data after a reasonable
              retention period.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">12. Changes to the Service or Terms</h2>
            <p className="mt-2 text-muted-foreground">
              We may modify the Service or these Terms from time to time. Material changes will be
              communicated by email or in-app notice. Continued use after changes take effect
              constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">13. Governing law and disputes</h2>
            <p className="mt-2 text-muted-foreground">
              These Terms are governed by the laws applicable at legaldecoder's principal place of
              business, without regard to conflict-of-laws rules. The courts of that jurisdiction
              have exclusive jurisdiction over any disputes, except where mandatory
              consumer-protection law in your country grants you the right to bring proceedings
              locally.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">14. Force majeure and assignment</h2>
            <p className="mt-2 text-muted-foreground">
              Neither party is liable for failure to perform due to events beyond reasonable
              control. You may not assign these Terms without our consent; we may assign them in
              connection with a merger, acquisition, or sale of assets.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">15. Contact</h2>
            <p className="mt-2 text-muted-foreground">
              Questions about these Terms:{" "}
              <a className="underline" href="mailto:legal@legaldecoder.io">
                legal@legaldecoder.io
              </a>
              . For payment, billing, or refund inquiries handled by Paddle, visit{" "}
              <a className="underline" href="https://paddle.net" target="_blank" rel="noreferrer">
                paddle.net
              </a>
              .
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
